import React from 'react';
import {ActivityIndicator, StyleSheet, View} from 'react-native';
import {LayoutAnimationConfig} from 'react-native-reanimated';

import type {OrderLine} from '../../../transport/apiServiceResponse';

import CartLineItem from './CartLineItem';

export interface CartItemsListProps {
  /** Order lines from cartStore.currentOrder.order_lines */
  orderLines?: OrderLine[];
  /** Whether items can be modified (quantity change, delete) - default true */
  editable?: boolean;
  /** Disable interactions during order submission */
  disabled?: boolean;
  /** Gap between items - default 8 */
  gap?: number;
  /** Component to render when list is empty */
  emptyComponent?: React.ReactNode;
  /** Show loading indicator instead of list */
  isLoading?: boolean;
  /** Test ID for the container */
  testID?: string;
}

/**
 * Renders a list of cart items with proper animation handling.
 * Filters out charge lines (delivery fees, etc.) and displays product lines only.
 *
 * Uses LayoutAnimationConfig skipEntering to prevent Reanimated + React 19
 * invisible render issue (see GOTCHAS.md #10).
 *
 * Consolidates duplicated rendering from:
 * - CartScreenLoyaltyEnabled.tsx (renderOrderItems)
 * - CartContent.tsx
 * - CheckoutReviewScreen.tsx
 * - ReviewContent.tsx
 */
export function CartItemsList({
  orderLines,
  editable = true,
  disabled = false,
  gap = 8,
  emptyComponent = null,
  isLoading = false,
  testID = 'cart-items-list',
}: CartItemsListProps) {
  // Show loading indicator when loading
  if (isLoading) {
    return (
      <View style={styles.loadingContainer} testID={`${testID}-loading`}>
        <ActivityIndicator size="large" />
      </View>
    );
  }

  // Filter out charge lines (delivery fees, etc.)
  const productLines = orderLines?.filter((line: OrderLine) => !line.charge) || [];

  if (!productLines.length) {
    return <>{emptyComponent}</>;
  }

  return (
    <LayoutAnimationConfig skipEntering>
      <View style={[styles.container, {gap}]} testID={testID}>
        {productLines.map((item: OrderLine, index: number) => (
          <CartLineItem
            item={item}
            index={index}
            key={item.product_id}
            disabled={disabled || !editable}
          />
        ))}
      </View>
    </LayoutAnimationConfig>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  loadingContainer: {
    paddingVertical: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default CartItemsList;
